import { groupWords, formatGroupNumber } from 'utils/groupLabel';
import { jsPDF } from 'jspdf';
import bwipjs from 'bwip-js';

// Bib ID label sheets for club athletes. Fully client-side: bwip-js renders
// each Data Matrix (ECC 200) code to an offscreen canvas, jsPDF lays the
// labels out on an Avery-format grid and triggers the download. The timing
// app scans the code (Netum C750 2D imagers) to resolve the athlete, so the
// encoded value must be the bare digits of clubAthleteNumber — no prefix,
// no padding.

// All dimensions in mm. pitchX/pitchY are label-origin to label-origin
// (label size + inter-label gap); margins locate the first label's top-left.
export const LABEL_SHEETS = [
  {
    id: 'L7157',
    label: 'A4 — Avery L7157 (33 per sheet)',
    pageFormat: 'a4',
    labelWidth: 64.0,
    labelHeight: 24.3,
    marginLeft: 6.5,
    marginTop: 14.85,
    pitchX: 66.5,
    pitchY: 24.3,
    columns: 3,
    rows: 11,
  },
  {
    id: '5160',
    label: 'US Letter — Avery 5160 (30 per sheet)',
    pageFormat: 'letter',
    labelWidth: 66.675,
    labelHeight: 25.4,
    marginLeft: 4.7625,
    marginTop: 12.7,
    pitchX: 69.85,
    pitchY: 25.4,
    columns: 3,
    rows: 10,
  },
];

export const labelsPerSheet = (sheet) => sheet.columns * sheet.rows;

const MM_PER_PT = 0.3528;
// Baseline offset within a text line's slot, as a fraction of the font size.
const ASCENT = 0.75;

// One layout serves every sheet: it is sized for the shortest label (Avery
// L7157, 24.3 mm tall) and both the text block and the code are vertically
// centred, so taller labels just get more clear space. The Data Matrix
// prints at codeSize with a quietZone clear border on every side
// (12 + 2 × 1 = 14 mm outer box).
const LAYOUT = {
  padX: 3,
  codeSize: 12,
  quietZone: 1,
  codeMarginRight: 1.5,
  nameSize: 12,
  nameMinSize: 10,
  infoSize: 12,
  gapNameInfo: 2,
};

// Pixels per Data Matrix module on the offscreen canvas. Bib numbers up to
// 6 digits yield a 10×10 symbol, so 24 px/module ≈ 240 px across the 12 mm
// printed code ≈ 500 dpi — the modules stay crisp on paper.
const MODULE_PX = 24;

const getDataMatrix = (cache, value) => {
  const cached = cache.get(value);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  bwipjs.toCanvas(canvas, {
    bcid: 'datamatrix',
    text: value,
    scale: MODULE_PX,
  });
  const dataUrl = canvas.toDataURL('image/png');
  cache.set(value, dataUrl);
  return dataUrl;
};

// Shrink the font until the text fits, then truncate with an ellipsis as a
// last resort. Leaves the doc's font size set to the returned size.
const fitText = (doc, text, maxWidth, startSize, minSize) => {
  let size = startSize;
  doc.setFontSize(size);
  while (size > minSize && doc.getTextWidth(text) > maxWidth) {
    size -= 0.5;
    doc.setFontSize(size);
  }
  let out = text;
  while (out.length > 1 && doc.getTextWidth(`${out}…`) > maxWidth) {
    out = out.slice(0, -1).trimEnd();
  }
  return { text: out === text ? text : `${out}…`, size };
};

// The width a run of text takes up on the page. jsPDF's getTextWidth takes
// kerning off (it pulls "Y" and "e" together, for one), but the text is drawn
// without kerning, so its figure can be narrower than what is printed: "Year "
// measured 0.6 mm short, which swallowed half the space before the number.
// One character has no neighbour to kern with, so the sum of the characters
// is the width as drawn. "Age " and the numbers measure the same either way.
const drawnTextWidth = (doc, text) =>
  [...text].reduce((width, char) => width + doc.getTextWidth(char), 0);

const drawLabel = (doc, athlete, sheet, x, y, codeCache, groupingBasis) => {
  const numberText = String(athlete.clubAthleteNumber);

  const boxSize = LAYOUT.codeSize + 2 * LAYOUT.quietZone;
  const boxLeft = x + sheet.labelWidth - LAYOUT.codeMarginRight - boxSize;
  doc.addImage(
    getDataMatrix(codeCache, numberText),
    'PNG',
    boxLeft + LAYOUT.quietZone,
    y + (sheet.labelHeight - LAYOUT.codeSize) / 2,
    LAYOUT.codeSize,
    LAYOUT.codeSize
  );

  const textLeft = x + LAYOUT.padX;
  // The code box's quiet zone doubles as the gap between text and code.
  const maxTextWidth = boxLeft - textLeft;

  const nameHeight = LAYOUT.nameSize * MM_PER_PT;
  const infoHeight = LAYOUT.infoSize * MM_PER_PT;
  const blockHeight = nameHeight + LAYOUT.gapNameInfo + infoHeight;
  let cursorY = y + (sheet.labelHeight - blockHeight) / 2;

  doc.setFont('helvetica', 'bold');
  const name = fitText(
    doc,
    athlete.athleteName,
    maxTextWidth,
    LAYOUT.nameSize,
    LAYOUT.nameMinSize
  );
  doc.text(name.text, textLeft, cursorY + nameHeight * ASCENT);
  cursorY += nameHeight + LAYOUT.gapNameInfo;

  // Officials look for the age first, so the age number and gender are bold.
  // Mixed weights need per-segment draws. A club that groups by year level
  // prints "Year 5" where an age club prints "Age 11" (utils/groupLabel.js).
  doc.setFontSize(LAYOUT.infoSize);
  const segments = [
    { text: `${groupWords(groupingBasis).value} `, bold: false },
    { text: formatGroupNumber(groupingBasis, athlete.ageGroup), bold: true },
    { text: '  ·  ', bold: false },
    { text: (athlete.gender || '').toUpperCase(), bold: true },
  ];
  const infoBaseline = cursorY + infoHeight * ASCENT;
  let infoX = textLeft;
  segments.forEach((segment) => {
    doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
    doc.text(segment.text, infoX, infoBaseline);
    infoX += drawnTextWidth(doc, segment.text);
  });
};

// athletes: rows from the Athletes page (athleteName, ageGroup, gender,
// clubAthleteNumber) — the caller filters out athletes without a bib number.
// startPosition is the 1-based label slot on the first sheet, so a partly
// used sheet can be loaded again. groupingBasis is how the club groups its
// athletes (utils/groupLabel.js); without it the labels read as an age club's.
// Returns { labelCount, sheetCount }.
export const generateAthleteLabelsPdf = ({
  athletes,
  sheet,
  startPosition = 1,
  copies = 1,
  fileName = 'athlete-labels.pdf',
  groupingBasis,
}) => {
  const doc = new jsPDF({
    unit: 'mm',
    format: sheet.pageFormat,
    orientation: 'portrait',
  });
  const codeCache = new Map();
  const perPage = labelsPerSheet(sheet);

  // Age, then gender (F before M, matching the meet pages' group order),
  // then name — so a printed stack follows the same order officials work in.
  const sorted = [...athletes].sort(
    (a, b) =>
      Number(a.ageGroup) - Number(b.ageGroup) ||
      String(a.gender || '').localeCompare(String(b.gender || '')) ||
      a.athleteName.localeCompare(b.athleteName)
  );
  const entries = sorted.flatMap((a) => Array(copies).fill(a));

  let currentPage = 0;
  entries.forEach((athlete, i) => {
    const position = startPosition - 1 + i;
    const page = Math.floor(position / perPage);
    if (page > currentPage) {
      doc.addPage();
      currentPage = page;
    }
    const slot = position % perPage;
    const x = sheet.marginLeft + (slot % sheet.columns) * sheet.pitchX;
    const y =
      sheet.marginTop + Math.floor(slot / sheet.columns) * sheet.pitchY;
    drawLabel(doc, athlete, sheet, x, y, codeCache, groupingBasis);
  });

  doc.save(fileName);
  return { labelCount: entries.length, sheetCount: currentPage + 1 };
};
